/**
 * Horizontal bar of "object-set contribution" chips, sitting directly under
 * the topbar, above the lens canvas.
 *
 * Each chip represents one batch of entities the user added (insight,
 * agent tool, neighbour expansion). Toggle the chip to temporarily hide
 * its nodes from the canvas without deleting them. The "×" drops the
 * contribution: the chip disappears AND the entities it added are removed
 * from the investigation's graph view.
 *
 * Renders nothing when there are no contributions for the current
 * viewpoint, so the bar collapses to zero height by default and only
 * shows up once the user starts composing the object set.
 */

import { Sparkles, User, X, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useInvestigationsStore,
  useInvestigationsStoreApi,
} from '@/stores/investigations-store-provider'
import {
  type ContributionSource,
  type ObjectSetContribution,
  useContributionsFor,
  useObjectSetContributionsStore,
} from '@/stores/object-set-contributions-store'
import { cn } from '@/utils/cn'

const SOURCE_ICON: Record<ContributionSource, typeof Sparkles> = {
  insight: Zap,
  agent: Sparkles,
  expand: User,
}

const SOURCE_LABEL: Record<ContributionSource, string> = {
  insight: 'Insight',
  agent: 'Agent',
  expand: 'Expand',
}

interface ObjectSetChipsBarProps {
  className?: string
  view: 'graph' | 'map' | 'timeline'
}

const VIEW_LABEL_KEY: Record<ObjectSetChipsBarProps['view'], string> = {
  graph: 'investigations.graphView.label',
  map: 'investigations.graphView.mapLabel',
  timeline: 'investigations.graphView.timelineLabel',
}

export function ObjectSetChipsBar({ className, view }: ObjectSetChipsBarProps) {
  const { t } = useTranslation()
  const investigationKey = useInvestigationsStore(s =>
    s.activeId ? s.activeId : s.transient ? 'transient' : null
  )
  // useContributionsFor returns a stable reference for the empty case so the
  // Zustand snapshot stays equal across renders; passing a raw selector that
  // does `?? []` would create a new array every call → infinite-loop crash.
  const contributions = useContributionsFor(investigationKey ?? undefined)
  const toggle = useObjectSetContributionsStore(s => s.toggle)
  const remove = useObjectSetContributionsStore(s => s.remove)
  const investigationsStoreApi = useInvestigationsStoreApi()

  if (!investigationKey || contributions.length === 0) return null

  return (
    <div
      data-slot="object-set-chips-bar"
      className={cn(
        'flex shrink-0 items-center gap-2 border-b border-border bg-card px-4 py-2',
        className
      )}
    >
      <span className="text-3xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {t(VIEW_LABEL_KEY[view])}
      </span>
      <span className="size-1 rounded-full bg-border" />
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        {contributions.map(c => (
          <ContributionChip
            key={c.id}
            contribution={c}
            onToggle={() => toggle(investigationKey, c.id)}
            onRemove={() => {
              investigationsStoreApi.getState().removeFromObjectSet(c.nodeIds, c.edgeIds)
              remove(investigationKey, c.id)
            }}
          />
        ))}
      </div>
    </div>
  )
}

function ContributionChip({
  contribution,
  onToggle,
  onRemove,
}: {
  contribution: ObjectSetContribution
  onToggle: () => void
  onRemove: () => void
}) {
  const Icon = SOURCE_ICON[contribution.source]
  return (
    <span
      className={cn(
        'group/chip inline-flex items-center gap-1 rounded-full border px-2 py-1.5 md:py-0.5',
        'text-2xs transition-colors',
        contribution.enabled
          ? 'border-primary/40 bg-primary/10 text-primary'
          : 'border-border bg-muted/40 text-muted-foreground line-through'
      )}
      title={`${SOURCE_LABEL[contribution.source]} · ${contribution.nodeIds.length} entities`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center gap-1 outline-none"
        aria-label={`Toggle ${contribution.label}`}
      >
        <Icon className="size-3" />
        <span className="max-w-[18ch] truncate">{contribution.label}</span>
        <span className="font-mono tabular-nums opacity-70">{contribution.nodeIds.length}</span>
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 inline-flex size-6 items-center justify-center rounded-full text-muted-foreground/70 hover:bg-foreground/10 hover:text-foreground md:size-3.5"
        aria-label="Remove contribution"
      >
        <X className="size-2.5" />
      </button>
    </span>
  )
}
